import "firebase/auth";
import { jwtDecode } from "jwt-decode";
import { auth } from "../firebase/firebaseConfig";
import { cardTitles, questionTypes } from "./constants";

export const getUser = () => {
  const user = localStorage.getItem("user");
  return JSON.parse(user);
};

export const setUser = (user) => {
  localStorage.setItem("user", JSON.stringify(user));
};

export const refreshTokenIfExpired = async () => {
  try {
    const token = getUser()?.accessToken;
    if (!token) {
      console.error("No token found in local storage");
      return;
    }
    // Decode the token to get the expiration time
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now();

    // Check if the token is expired or about to expire in 5 minutes
    if (decodedToken.exp * 1000 <= currentTime + 5 * 60 * 1000) {
      console.log("Token is expired or about to expire. Refreshing token...");
      return new Promise((resolve, reject) => {
        auth.onAuthStateChanged(async (user) => {
          if (user) {
            try {
              // Get a fresh token from Firebase
              const newToken = await user.getIdToken(true);
              // Store the refreshed token in localStorage
              setUser({
                ...getUser(),
                accessToken: newToken,
                stsTokenManager: { accessToken: newToken },
              });
              resolve(newToken);
              return newToken;
            } catch (error) {
              console.error("Error while refreshing token: ", error);
              reject(null);
            }
          } else {
            console.error("No user is currently signed in");
            reject(null);
          }
        });
      });
    } else {
      // console.log("Token is still valid.");
      return token;
    }
  } catch (error) {
    console.error("Error while refreshing token: ", error);
    return null;
  }
};

// Custom rectangle to ensure bars stay centered and have top radius
export const CenteredBarShape = (props) => {
  const { x, y, width, height, fill } = props;
  const radius = props.radius;

  // Calculate new x position to center the bar
  const newX = x + width / 2 - props.barWidth / 2;

  return (
    <path
      d={`
        M${newX},${y + height}
        L${newX},${y + radius}
        Q${newX},${y} ${newX + radius},${y}
        L${newX + props.barWidth - radius},${y}
        Q${newX + props.barWidth},${y} ${newX + props.barWidth},${y + radius}
        L${newX + props.barWidth},${y + height}
        Z
      `}
      fill={fill}
    />
  );
};

export const formatAnswer = (answer) => {
  if (
    answer?.type === questionTypes["mcq"] ||
    answer?.type === questionTypes["text"]
  ) {
    // if answer is mcq or text
    // return { text: { key: 1, value: answer?.text?.value } };
    return answer?.text[0]?.value;
  } else if (
    // if answer is range or number
    answer?.type === questionTypes["range"] ||
    answer?.type === questionTypes["number"]
  ) {
    // finalAnswer = { questionDetails.answer };
    // return { number: answer?.number };
    return answer?.number;
  } else if (answer?.type === questionTypes["checkbox"] ||
    answer?.type === questionTypes["multipleRange"]) {
    // if answer is checkbox
    // return { text: { key: 1, value: answer?.text?.value } };
    return answer?.text
  } else {
    return "";
  }
};

// Format number by adding commas
export const addCommasToNumber = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Format types of the graph 
export const formatGraphTypeNames = (type) => {
  const nameArray = type.split("-");
  return nameArray.map((name) => name.charAt(0).toUpperCase() + name.slice(1)).join(" ");
}


export const handleTitle = (order) => {
  switch (order) {
    case '4':
      return cardTitles[0];
    case '6':
      return cardTitles[1];
    case '9':
      return cardTitles[2];
    case '10':
      return cardTitles[3];
    default:
      return ''
  }
}

// if the value has 00 after decimal, make it integer
export const formatNumber = (number) => {
  if (number % 1 === 0) {
    return number;
  } else {
    return number.toFixed(2);
  }
}

// Convert number to thousand, million, billion and return the value that constains commas
export const convertNumberToThousand = (number) => {
  if (number === 0) {
    return 0
  }
  if (number >= 1000000000) {
    return addCommasToNumber(number / 1000000000) + ' Billion';
  } else if (number >= 1000000) {
    return addCommasToNumber(number / 1000000) + ' Million';
  } else {
    return addCommasToNumber(number);
  }
}

export const handleFloatInt = (value) => {
  if (value == 0)
    return 0
  const intValue = parseInt(value);
  if (value > intValue) {
    return value.toFixed(2);
  }
  return value;
}

export const clearUserData = () => {
  // Clear localStorage
  localStorage.clear();

  // Clear all cookies
  document.cookie.split(";").forEach(function (c) {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });

  // Clear sessionStorage if needed
  sessionStorage.clear();
};

/**
 * Extracts address components from Google Places API response
 * @param {Object} response - The Google Places API response
 * @returns {Object} Object containing extracted address components
 */
export const extractAddressComponents = (response) => {
  try {
    // Default values in case components are missing
    const addressInfo = {
      full_address: null,
      city: null,
      state: null,
      zipcode: null,
      country: null,
      street: null,
      street_number: null,
    };

    // Check if response and required nested objects exist
    if (!response?.body?.result) {
      throw new Error('Invalid response structure');
    }

    const result = response.body.result;

    // Extract full address from formatted_address field
    addressInfo.full_address = result.formatted_address || null;

    // Extract components from address_components array
    if (Array.isArray(result.address_components)) {
      result.address_components.forEach(component => {
        if (!component.types || !Array.isArray(component.types)) {
          return;
        }

        // Extract city from locality or sublocality
        if (component.types.includes('locality') ||
          component.types.includes('sublocality')) {
          addressInfo.city = component.long_name;
        }
        // Extract state
        else if (component.types.includes('administrative_area_level_1')) {
          addressInfo.state = component.short_name;
        }
        // Extract postal code
        else if (component.types.includes('postal_code')) {
          addressInfo.zipcode = component.long_name;
        }
        // Extract country
        else if (component.types.includes('country')) {
          addressInfo.country = component.long_name;
        }
        // Extract street
        else if (component.types.includes('route')) {
          addressInfo.street = component.long_name;
        }
        // Extract street number
        else if (component.types.includes('street_number')) {
          addressInfo.street_number = component.long_name;
        }
      });
    }

    return addressInfo;

  } catch (error) {
    console.error('Error extracting address components:', error);
    return {
      full_address: null,
      city: null,
      state: null,
      zipcode: null,
      country: null,
      street: null,
      street_number: null,
    };
  }
}
